import type { SxProps } from "@mui/material";
import { alpha, type Theme, useTheme } from "@mui/material/styles";
import { type GridColumnVisibilityModel, type GridRowParams, useGridApiRef } from "@mui/x-data-grid";
import type { DateTime } from "luxon";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { useRevealWage } from "../../hooks/useRevealWage";
import { GRID_ROW_HEIGHT, NEUTRAL, STAFF_PICKER, STATUS } from "../../theme/tokens";
import type { NurseData, Request, ShiftRequest } from "../../utils/models";
import type { SortingState } from "../../utils/multiColumnSorting";
import { useRequestUtils } from "../RequestData/Provider/useRequestUtils";
import type { SearchableColumn } from "../UIToolkit/Tables/SystemDataGrid";
import { SystemDataGrid } from "../UIToolkit/Tables/SystemDataGrid";
import type { ColumnPickerOptions, RowGroupingOptions, SearchWidthRange, SystemDataGridProps } from "../UIToolkit/Tables/SystemDataGrid/types";
import { buildNursesMatrixColumns, SELECTED_CHECK_CLASS } from "./columns";
import { nurseTypeDefaultVisibility, requestedAtDefaultVisibility } from "./columnVisibility";
import { NURSES_MATRIX_ANALYTICS_SOURCE, type NursesMatrixRowId, type NursesMatrixSelectionMode } from "./consts";
import { getDayColumnField, HIGHLIGHTED_COLUMN_CLASS, highlightedColumnSx } from "./dayColumns";
import { useControlledSelection } from "./useControlledSelection";
import { useNursesMatrixKeyboardNav } from "./useNursesMatrixKeyboardNav";

/** Debounce before a search keystroke is reported to analytics — avoids one event per character. */
const SEARCH_TRACK_DEBOUNCE_MS = 600;

const gridSx = {
	"& .MuiDataGrid-row": { cursor: "pointer" },
	// Partial-shift rows: light red tint (token replaces the legacy #FFE5E5 literal).
	"& .MuiDataGrid-row.partial-row": { backgroundColor: STATUS.ERROR.light },
	"& .MuiDataGrid-row.unselectable-row": { cursor: "not-allowed", opacity: 0.7, "& .MuiDataGrid-cell": { color: NEUTRAL[500] } },
	"& .MuiDataGrid-row.denied-row": { cursor: "not-allowed", opacity: 0.5, "& .MuiDataGrid-cell": { textDecoration: "line-through" } },
	// The committed pick is marked by the CheckCircle in the name cell, revealed purely via this row
	// class — so committing a row flips a class instead of rebuilding the column set.
	[`& .MuiDataGrid-row.selected-row .${SELECTED_CHECK_CLASS}`]: { display: "inline-flex" },
	// Cursor row wins over partial (declared later = higher-order at equal specificity). `selected-row`
	// carries no tint — the committed pick is marked by the CheckCircle in the name cell.
	"& .MuiDataGrid-row.cursor-row, & .MuiDataGrid-row.cursor-row:hover": { backgroundColor: NEUTRAL[400] },
	// Highlighted day column rails (brand left/right on cells + header top cap) — shared with the minimized
	// picker via `highlightedColumnSx`.
	...highlightedColumnSx,
} as const satisfies SxProps;

/**
 * Row looks for the controlled selection modes only — merged over `gridSx` when `selectionMode` is set, so
 * the legacy picker's sx is untouched. Picked rows take a soft brand wash (derived from the org's primary),
 * the keyboard cursor is a brand rail rather than the legacy grey fill (a grey block would read as a
 * second selection next to the wash), and blocked rows sit on a quiet neutral field.
 */
const selectionModeSx = (theme: Theme) =>
	({
		"& .MuiDataGrid-row.checked-row": { backgroundColor: alpha(theme.palette.primary.main, STAFF_PICKER.SELECTED_ROW_WASH_ALPHA) },
		"& .MuiDataGrid-row.checked-row:hover": { backgroundColor: alpha(theme.palette.primary.main, STAFF_PICKER.SELECTED_ROW_HOVER_WASH_ALPHA) },
		// The highlighted day's own cell fill would break the wash mid-row; let the row's wash run through (rails stay).
		[`& .MuiDataGrid-row.checked-row .MuiDataGrid-cell.${HIGHLIGHTED_COLUMN_CLASS}`]: { backgroundColor: "transparent" },
		"& .MuiDataGrid-row.picker-cursor-row": { boxShadow: `inset ${STAFF_PICKER.CURSOR_RAIL_WIDTH}px 0 0 ${theme.palette.primary.main}` },
		"& .MuiDataGrid-row.blocked-row, & .MuiDataGrid-row.blocked-row:hover": { cursor: "not-allowed", backgroundColor: NEUTRAL[50] },
	}) as const;

/** Synthetic-picker id + persisted visibility key namespace for the single "Days" toggle. */
const DAYS_TOGGLE_ID = "days";

/**
 * The legacy picker: one committed row marked by a CheckCircle in the name cell, reported through
 * `onSelectNurse`. Every caller that doesn't pass `selectionMode` gets exactly this.
 */
interface LegacySelectionProps {
	selectionMode?: undefined;
	/** Called with the committed row, or `null` when the selection is cleared (re-select toggle or auto-clear). */
	onSelectNurse?: (nurse: NurseData | null) => void;
	/**
	 * When true, committing the already-selected row clears the selection (toggle). Consumers whose action
	 * button gates on their own selection state (approval/removal) pass this so the grid's checkmark and the
	 * parent's state stay in sync; add/move flows leave it off (a re-click re-commits).
	 */
	deselectOnReselect?: boolean;
	selectedRowIds?: never;
	onSelectionChange?: never;
	pendingDayIndex?: never;
}

/**
 * Controlled picking: a leading radio (`"single"`) or checkbox (`"multiple"`) column; the parent owns the
 * picked ids. Row click / Enter select (single) or toggle (multiple); Space toggles in multiple mode.
 */
interface ControlledSelectionProps {
	selectionMode: NursesMatrixSelectionMode;
	/** Picked grid row ids (`rowKey ?? id`). Ids that stop being selectable are pruned via `onSelectionChange`. */
	selectedRowIds: ReadonlyArray<NursesMatrixRowId>;
	/** The full next selection, as rows, in pick order. Fires on every pick and when a pick is pruned. */
	onSelectionChange: (rows: NurseData[]) => void;
	/** Day index (into `dateRange`) whose cell shows the dashed "+" preview on every picked row. */
	pendingDayIndex?: number;
	onSelectNurse?: never;
	deselectOnReselect?: never;
}

interface NursesMatrixGridBaseProps {
	nursesData: NurseData[];
	dateRange: DateTime[];
	highlightDateIndexes?: number[];
	isAdd: boolean;
	filterDate?: DateTime;
	request?: Request | ShiftRequest;
	/** When set, renders a trailing deny/restore action column per row. */
	onDenyNurse?: (nurseId: number) => void;
	onRestoreNurse?: (nurseId: number) => void;
	/** Nurses queued for deny this session — struck through and rendered with a restore action. */
	queuedDenyNurseIds?: number[];
	hightlightOriginalShift?: boolean;
	/** Persists the grid's multi-sort state per-user. */
	localStorageTableKey?: string;
	/**
	 * Renders the toolbar column picker, which toggles the optional metadata columns plus a single "Days"
	 * row that shows/hides all day columns as one block (name and the deny action stay fixed). Off by
	 * default so secondary instances (e.g. Move/Swap) are unaffected. Column visibility persists per-user
	 * when `localStorageTableKey` is set, session-only otherwise.
	 */
	enableColumnPicker?: boolean;
	/** Shows skeleton rows while the caller's row/prediction data loads. */
	loading?: boolean;
	/** Node rendered in the no-rows overlay (e.g. an `EmptyState`). */
	emptyState?: ReactNode;
	/** Columns the grid's built-in search filters on (e.g. `["name"]`). When set, a search box renders in the toolbar. */
	searchableColumns?: Array<SearchableColumn<NurseData>>;
	/** Placeholder for the built-in search box. */
	searchPlaceholder?: string;
	/** Width (px) of the search box — narrow it, or give it a flex range, when `leadingToolbarNodes` shares the toolbar row. */
	searchWidth?: number | SearchWidthRange;
	/** Extra controls (e.g. role/contract filters) rendered inline in the grid toolbar, alongside the search + sort button. */
	toolbarNodes?: ReactNode;
	/**
	 * Content at the START of the toolbar row, before the search box — e.g. the step heading of the panel the
	 * grid sits in, so heading and controls share one line. Give it `flexGrow: 1` to push the controls right.
	 */
	leadingToolbarNodes?: ReactNode;
	/**
	 * Column visibility a surface wants on first load, merged OVER the grid's own defaults (e.g.
	 * `{ nurseType: true }` to show the Contract column that ships hidden). Only the initial state: the
	 * user's column-picker choices still win once saved. Requires `enableColumnPicker`.
	 */
	defaultColumnVisibility?: GridColumnVisibilityModel;
	/**
	 * Enables the arrow/Enter keyboard picker (default true). Set false for read-only grids or secondary
	 * instances that shouldn't attach their own window-level keydown listener (avoids double-hijacking keys).
	 */
	enableKeyboardNav?: boolean;
	/**
	 * Buckets rows under `SystemDataGrid` bands (e.g. a folded "3 can't work this shift" band after the
	 * available rows, which sit in a `bandlessKeys` group). Passed straight through.
	 */
	rowGrouping?: RowGroupingOptions<NurseData>;
	/**
	 * Size the grid to its rows between a min and max, then scroll — for a grid inside a content-sized popup,
	 * which has no fixed height for it to fill. Passed straight through to `SystemDataGrid`.
	 */
	containedHeight?: SystemDataGridProps<NurseData>["containedHeight"];
	dataTestId?: string;
}

export type NursesMatrixGridProps = NursesMatrixGridBaseProps & (LegacySelectionProps | ControlledSelectionProps);

/**
 * Row identity. `id` is the NURSE id — NOT unique when a nurse is offered as several candidates (MoveNursePanel
 * lists a nurse once per eligible source/target day). `rowKey` disambiguates those; it falls back to `id` for the
 * common one-row-per-nurse case.
 */
const getRowId = (row: NurseData): string | number => row.rowKey ?? row.id;

/**
 * Cross-tab nurse picker built on the canonical `SystemDataGrid`. Replaces the legacy
 * `NursesMatrixTable` (raw MUI `Table` + hand-rolled windowing). Virtualization, multi-sort,
 * loading/empty states, and the shared look come from `SystemDataGrid`; this component owns the
 * dynamic column set, the committed/cursor selection state, and the keyboard picker.
 */
export function NursesMatrixGrid(props: NursesMatrixGridProps) {
	const {
		nursesData,
		dateRange,
		highlightDateIndexes,
		onSelectNurse,
		isAdd,
		filterDate,
		request,
		onDenyNurse,
		onRestoreNurse,
		queuedDenyNurseIds,
		hightlightOriginalShift = false,
		localStorageTableKey,
		enableColumnPicker = false,
		loading,
		emptyState,
		searchableColumns,
		searchPlaceholder,
		searchWidth,
		toolbarNodes,
		leadingToolbarNodes,
		defaultColumnVisibility,
		enableKeyboardNav = true,
		deselectOnReselect = false,
		rowGrouping,
		containedHeight,
		dataTestId = "nurses-matrix-grid",
	} = props;
	const selectionMode = props.selectionMode;
	const { track } = useAnalytics();
	const { t } = useTranslation();
	const theme = useTheme();
	const canViewWage = useRevealWage();
	const { isHospitalView, isMedicalDepartment } = useHospitalData();
	const { isPartialShift } = useRequestUtils();
	const isJobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);

	const apiRef = useGridApiRef();

	// `selectedRowId` is the committed pick (click / Enter) — marked by the CheckCircle in the name cell.
	// `cursorRowId` is the visual position arrow keys move before commit — it drives the gray row tint only.
	// Both hold the grid ROW id (getRowId), which equals the nurse id except for duplicate-candidate rows.
	const [selectedRowId, setSelectedRowId] = useState<string | number | null>(null);
	const [cursorRowId, setCursorRowId] = useState<string | number | null>(null);

	const queuedDenyIdSet = useMemo(() => new Set(queuedDenyNurseIds ?? []), [queuedDenyNurseIds]);

	// Apply the scheduled-deactivation gate: a nurse deactivating before `filterDate` becomes unselectable.
	const processedNursesData = useMemo<NurseData[]>(() => {
		return nursesData.map((nurse) => {
			const scheduledDeactivationCheck = filterDate && nurse.scheduledDeactivationDate && nurse.scheduledDeactivationDate < filterDate.startOf("day");
			// A row that states why it can't take the shift is never pickable, whatever `selectable` says.
			return { ...nurse, selectable: nurse.selectable !== false && !scheduledDeactivationCheck && nurse.unavailableReason == null };
		});
	}, [nursesData, filterDate]);

	const getIsPartial = useCallback(
		(nurseId: number): boolean => (request && "message" in request ? isPartialShift(request, nurseId) : false),
		[request, isPartialShift],
	);

	const { columns, sortingOptions } = useMemo(
		() =>
			buildNursesMatrixColumns({
				nursesData: processedNursesData,
				dateRange,
				highlightDateIndexes: highlightDateIndexes ?? [],
				isAdd,
				getIsPartial,
				hightlightOriginalShift,
				canViewWage,
				isJobManagementEnabled,
				// Strict inhouse2+Hospital flag — only the real hospital demo gets the dark day/night icons.
				darkShiftIcons: isHospitalView,
				isMedicalDepartment,
				onDenyNurse,
				onRestoreNurse,
				queuedDenyIdSet,
				primaryColor: theme.palette.primary.main,
				t,
				dataTestId,
				selectionMode,
				pendingDayIndex: props.pendingDayIndex,
			}),
		// `selectedRowId` is intentionally NOT a dep: selection is reflected via a row CSS class (see gridSx),
		// so a click no longer rebuilds every column (only the row's class flips).
		[
			processedNursesData,
			dateRange,
			highlightDateIndexes,
			isAdd,
			getIsPartial,
			hightlightOriginalShift,
			canViewWage,
			isJobManagementEnabled,
			isHospitalView,
			isMedicalDepartment,
			onDenyNurse,
			onRestoreNurse,
			queuedDenyIdSet,
			theme.palette.primary.main,
			t,
			dataTestId,
			selectionMode,
			props.pendingDayIndex,
		],
	);

	// Clear/repair selection + cursor when the selectable set changes (filter/search/re-sort). When the
	// committed pick disappears, notify the parent (onSelectNurse(null)) so its mirrored selection state
	// doesn't go stale (e.g. an "Add" button that would otherwise stay enabled for a now-unselectable nurse).
	useEffect(() => {
		const selectableRowIds = new Set(processedNursesData.filter((n) => n.selectable !== false).map((n) => getRowId(n)));
		if (selectableRowIds.size === 0) {
			if (selectedRowId != null) onSelectNurse?.(null);
			setSelectedRowId(null);
			setCursorRowId(null);
			return;
		}
		if (selectedRowId != null && !selectableRowIds.has(selectedRowId)) {
			onSelectNurse?.(null);
			setSelectedRowId(null);
		}
		if (cursorRowId != null && !selectableRowIds.has(cursorRowId)) setCursorRowId(null);
	}, [processedNursesData, selectedRowId, cursorRowId, onSelectNurse]);

	// Column-picker config: a single "Days" synthetic row toggling every day column as one block. Memoized
	// on the day count (dateRange length) so its `controlledFields` list stays stable across renders.
	const columnPickerConfig = useMemo<ColumnPickerOptions>(
		() => ({
			syntheticColumns: [{ id: DAYS_TOGGLE_ID, label: t("nurses.matrix.days"), controlledFields: dateRange.map((_, index) => getDayColumnField(index)) }],
			defaultVisibility: { ...nurseTypeDefaultVisibility, ...requestedAtDefaultVisibility, ...defaultColumnVisibility },
		}),
		[dateRange, t, defaultColumnVisibility],
	);

	const searchTestId = `${dataTestId}-search`;

	const controlled = useControlledSelection({
		selection: props.selectionMode ? { mode: props.selectionMode, selectedRowIds: props.selectedRowIds, onSelectionChange: props.onSelectionChange } : null,
		rows: processedNursesData,
		getRowId,
		queuedDenyIdSet,
		setCursorRowId,
		track,
	});

	// The pick rides on the rows handed to the grid, not on `processedNursesData`: the columns are built from
	// `processedNursesData`, so keeping the flag off it means a tick never rebuilds them.
	const gridRows = useMemo<NurseData[]>(
		() => (selectionMode ? processedNursesData.map((nurse) => ({ ...nurse, picked: controlled.selectedIdSet.has(getRowId(nurse)) })) : processedNursesData),
		[processedNursesData, selectionMode, controlled.selectedIdSet],
	);

	useNursesMatrixKeyboardNav({
		rows: processedNursesData,
		apiRef,
		getRowId,
		cursorRowId,
		setCursorRowId,
		selectedRowId,
		setSelectedRowId,
		onSelectNurse,
		deselectOnReselect,
		queuedDenyIdSet,
		enabled: enableKeyboardNav,
		track,
		searchTestId,
		selectionMode,
		onPickRow: controlled.pickRow,
	});

	const handleRowClick = useCallback(
		(params: GridRowParams<NurseData>) => {
			const nurse = params.row;
			if (selectionMode) {
				controlled.pickRow(nurse);
				return;
			}
			const isDenied = queuedDenyIdSet.has(nurse.id) || nurse.isDenied === true;
			if (isDenied || nurse.selectable === false) return;
			const rowId = getRowId(nurse);
			if (deselectOnReselect && rowId === selectedRowId) {
				setSelectedRowId(null);
				setCursorRowId(null);
				onSelectNurse?.(null);
				return;
			}
			setSelectedRowId(rowId);
			setCursorRowId(rowId);
			// No `nurse_name`: keep the event to non-PII identifiers only (id/role/type) — the name isn't needed
			// for debugging or segmentation and shouldn't be shipped to third-party analytics.
			track("staffing_matrix_select_nurse", {
				source: NURSES_MATRIX_ANALYTICS_SOURCE,
				nurse_id: nurse.id,
				nurse_role: nurse.role,
				nurse_type: nurse.nurseType,
			});
			onSelectNurse?.(nurse);
		},
		[onSelectNurse, queuedDenyIdSet, track, deselectOnReselect, selectedRowId, selectionMode, controlled.pickRow],
	);

	const getRowClassName = useCallback(
		(params: GridRowParams<NurseData>): string => {
			const nurse = params.row;
			const rowId = getRowId(nurse);
			const isDenied = queuedDenyIdSet.has(nurse.id) || nurse.isDenied === true;
			const classes: string[] = [];
			if (rowId === selectedRowId) classes.push("selected-row");
			if (selectionMode && controlled.selectedIdSet.has(rowId)) classes.push("checked-row");
			if (rowId === cursorRowId) classes.push(selectionMode ? "picker-cursor-row" : "cursor-row");
			if (isDenied) classes.push("denied-row");
			else if (nurse.unavailableReason != null) classes.push("blocked-row");
			else if (nurse.selectable === false) classes.push("unselectable-row");
			if (getIsPartial(nurse.id)) classes.push("partial-row");
			return classes.join(" ");
		},
		[cursorRowId, selectedRowId, queuedDenyIdSet, getIsPartial, selectionMode, controlled.selectedIdSet],
	);

	const dataGridProps = useMemo(
		() => ({
			hideFooter: true,
			// Both `pageSize: -1` ("All") AND `hideFooter` are required: SystemDataGrid's base defaults to
			// pageSize 50 + a footer, so hideFooter alone would strand rows past 50. -1 renders every row on a
			// single virtualized page (continuous internal scroll). Precedent: DepartmentUsersDialog.
			pageSizeOptions: [{ value: -1, label: "All" }],
			initialState: { pagination: { paginationModel: { pageSize: -1 } } },
			onRowClick: handleRowClick,
			getRowClassName,
			// The selection modes are a picker the user reads row by row, with a control in every row — the
			// standard density gives each row a comfortable click target. The legacy grid keeps compact rows.
			// `density` too: the base grid is compact, and MUI scales `rowHeight` by the density factor.
			...(selectionMode && { density: "standard" as const, rowHeight: GRID_ROW_HEIGHT.STANDARD, columnHeaderHeight: GRID_ROW_HEIGHT.STANDARD }),
		}),
		[handleRowClick, getRowClassName, selectionMode],
	);

	const sx = useMemo(() => (selectionMode ? [gridSx, selectionModeSx(theme)] : gridSx), [selectionMode, theme]);

	const defaultSortState = useMemo<SortingState<NurseData>[]>(
		() => [
			// Grid-owned default sort: name ascending, with `id` as a stable tie-break so equal names keep a
			// deterministic order. `name` is always the first column, so this default is universal across consumers.
			{ sortColumns: ["name"], sortOrder: "asc", label: t("name") },
			{ sortColumns: ["id"], sortOrder: "asc" },
		],
		[t],
	);

	// Report search usage to analytics (debounced), replacing the per-consumer "search nurse" event that
	// was lost when search moved into the shared grid toolbar.
	const searchTrackTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
	const handleSearchChange = useCallback(
		(value: string) => {
			if (searchTrackTimer.current) clearTimeout(searchTrackTimer.current);
			const trimmed = value.trim();
			if (!trimmed) return;
			searchTrackTimer.current = setTimeout(() => {
				track("staffing_matrix_search_nurse", { source: NURSES_MATRIX_ANALYTICS_SOURCE, query_length: trimmed.length });
			}, SEARCH_TRACK_DEBOUNCE_MS);
		},
		[track],
	);
	useEffect(
		() => () => {
			if (searchTrackTimer.current) clearTimeout(searchTrackTimer.current);
		},
		[],
	);

	return (
		<SystemDataGrid<NurseData>
			rows={gridRows}
			columns={columns}
			getRowId={getRowId}
			apiRef={apiRef}
			sortingOptions={sortingOptions}
			defaultSortState={defaultSortState}
			localStorageKey={localStorageTableKey}
			columnPicker={enableColumnPicker ? columnPickerConfig : undefined}
			loading={loading}
			emptyState={emptyState}
			searchableColumns={searchableColumns}
			searchPlaceholder={searchPlaceholder}
			searchWidth={searchWidth}
			onSearchChange={handleSearchChange}
			leadingToolbarNodes={leadingToolbarNodes}
			trailingToolbarNodes={toolbarNodes}
			dataGridProps={dataGridProps}
			rowGrouping={rowGrouping}
			containedHeight={containedHeight}
			sx={sx}
			dataTestId={dataTestId}
		/>
	);
}
